import React, { useCallback, useState, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import useExperiencesStore from "@/stores/tripBuilder/useExperiences";
import ExperienceSearch from "@tripbuilder/cat-experiences/topBar";
import ExperienceList from "@tripbuilder/cat-experiences/ExperienceList";
import ViatorActivityList from "@tripbuilder/cat-experiences/viator/viatorExperiences";
import MoreInfoModal from "@tripbuilder/cat-experiences/moreInfoModal";
import MoreInfoModalViator from "@tripbuilder/cat-experiences/viator/modal/moreInfoModalViator";
import CustomActivityForm from "@tripbuilder/cat-experiences/custom/addActivity";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useCurrencyStore from "@/stores/ui/currencyStore";
import { useViatorProducts } from "@/lib/experiences/viator-experience";
import { useWaytoraExperiences } from "@/lib/experiences/waytora-experiences";

interface ExperiencesContentProps {
  isAddDestinationModalOpen: boolean;
}

const ExperiencesContent: React.FC<ExperiencesContentProps> = ({
  isAddDestinationModalOpen,
}) => {
  const {
    waytoraExperience,
    setWaytoraExperience,
    viatorProduct,
    setViatorProduct,
    isModalOpen,
    setIsModalOpen,
    handleOpenModal,
    shouldCloseModal,
    activeExperiencesTab,
    setActiveExperiencesTab,
    selectedExperienceTabIndex,
    setSelectedExperienceTabIndex,
  } = useExperiencesStore();

  const { updateTripData, selectedLocation, checkIsAdmin } = useTripStore();
  const { selectedCurrency } = useCurrencyStore();
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [waytoraCurrentPage, setWaytoraCurrentPage] = useState<number>(0);
  const [viatorCurrentPage, setViatorCurrentPage] = useState<number>(0);
  const isAdmin = useMemo(() => checkIsAdmin(), [checkIsAdmin]);

  const handleTabChange = (index: number) => {
    if (index === 0) setActiveExperiencesTab("activities");
    else if (index === 1) setActiveExperiencesTab("experiences");
    else setActiveExperiencesTab("add-activity");
  };

  const handleFilterChange = useCallback((filter: string | null) => {
    setSelectedFilter(filter);
  }, []);

  const handleClose = (modalRef: React.RefObject<HTMLDivElement>) => {
    setIsModalOpen(false);
  };

  const {
    data: waytoraData,
    error: waytoraError,
    isFetching: isWaytoraFetching,
    status: waytoraStatus,
  } = useWaytoraExperiences(selectedLocation!.waytora_id, waytoraCurrentPage);

  const {
    data: viatorData,
    error: viatorError,
    isFetching: isViatorFetching,
    status: viatorStatus,
  } = useViatorProducts(
    selectedLocation!.viator_id,
    selectedLocation!.currency.viator,
    selectedCurrency.code,
    viatorCurrentPage
  );

  const handleLoadMore = (type: "viator" | "waytora") => {
    if (type === "viator" && viatorData?.nextCursor) {
      setViatorCurrentPage(viatorCurrentPage + 1);
    }
    if (type === "waytora" && waytoraData?.nextCursor) {
      setWaytoraCurrentPage(waytoraCurrentPage + 1);
    }
  };

  const renderContent = () => {
    switch (activeExperiencesTab) {
      case "experiences":
        return (
          <ExperienceList
            key="experiences"
            updateTripData={updateTripData}
            status={waytoraStatus}
            isWaytoraFetching={isWaytoraFetching}
            waytoraData={waytoraData ?? { data: [], nextCursor: null }}
            waytoraError={waytoraError}
            setSelectedExperience={setWaytoraExperience}
            handleOpenModal={handleOpenModal}
            handleLoadMore={handleLoadMore}
            currentPage={waytoraCurrentPage}
            setCurrentPage={setWaytoraCurrentPage}
          />
        );
      case "activities":
        return (
          <ViatorActivityList
            key="activities"
            updateTripData={updateTripData}
            status={viatorStatus}
            isViatorFetching={isViatorFetching}
            viatorData={viatorData ?? { data: [], nextCursor: null }}
            viatorError={viatorError}
            setViatorProduct={setViatorProduct}
            handleOpenModal={handleOpenModal}
            handleLoadMore={handleLoadMore}
            currentPage={viatorCurrentPage}
            setCurrentPage={setViatorCurrentPage}
          />
        );
      case "add-activity":
        return <CustomActivityForm />;
      default:
        return null;
    }
  };

  return (
    <section className="experiences-content w-full min-h-screen mt-2 flex flex-col items-center">
      <div
        className={`w-full ${
          isModalOpen ? "opacity-50 backdrop-blur-2xl" : ""
        }`}
      >
        <div className="py-2 rounded-xl w-full">
          <ExperienceSearch
            selectedExperienceTabIndex={selectedExperienceTabIndex}
            setSelectedExperienceTabIndex={setSelectedExperienceTabIndex}
            handleTabChange={handleTabChange}
            selectedFilter={selectedFilter}
            setSelectedFilter={handleFilterChange}
            setSelectedExperience={setWaytoraExperience}
            isAdmin={isAdmin}
          />
        </div>
        <div
          className={`experiences-list w-full mt-2 h-[calc(100vh-115px)] ${
            activeExperiencesTab !== "add-activity"
              ? "grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3"
              : ""
          } rounded-lg ${
            activeExperiencesTab !== "add-activity"
              ? "overflow-y-scroll no-scrollbar"
              : ""
          }`}
        >
          {renderContent()}
        </div>
      </div>

      <div className="flex items-center justify-center">
        <AnimatePresence mode="wait">
          {/* General Experiences Modal */}
          {isModalOpen && activeExperiencesTab === "experiences" && (
            <MoreInfoModal
              updateTripData={updateTripData}
              selectedExperience={waytoraExperience}
              placeId={waytoraExperience?.waytora_place_id || ""}
              handleClose={handleClose}
              isAddDestinationModalOpen={isAddDestinationModalOpen}
              shouldClose={shouldCloseModal}
              isAdmin={isAdmin}
            />
          )}

          {/* Viator Experiences Modal */}
          {isModalOpen && activeExperiencesTab === "activities" && (
            <MoreInfoModalViator
              updateTripData={updateTripData}
              product={viatorProduct}
              selectedLocation={selectedLocation!}
              handleClose={handleClose}
              isAddDestinationModalOpen={isAddDestinationModalOpen}
              shouldClose={shouldCloseModal}
              isAdmin={isAdmin}
            />
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ExperiencesContent;
